// src/broadcast/hooks/useMessageToast.ts
"use client"

import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import { useBroadcastStore } from '../stores/useBroadcastStore'
import type { MessageRecord } from '../types/events/message'

/**
 * Options for useMessageToast hook
 */
export interface UseMessageToastOptions {
  /**
   * Enable or disable toast notifications
   * @default true
   */
  enabled?: boolean

  /**
   * Filter function to selectively show toasts
   * Return true to show toast, false to suppress
   *
   * @example
   * // Exclude messages from current conversation
   * filter: (msg) => msg.conversation_id !== currentConversationId
   */
  filter?: (message: MessageRecord) => boolean

  /**
   * Custom toast duration in milliseconds
   * @default 4000
   */
  duration?: number
}

/**
 * Format message content for toast display
 */
function formatMessagePreview(message: MessageRecord): string {
  const maxLength = 60

  // Handle different message types
  switch (message.type) {
    case 'Media':
      return '📷 Sent media'
    case 'Composition':
      return '🎬 Shared a composition'
    case 'Message':
    default:
      if (!message.content) return 'New message'
      return message.content.length > maxLength
        ? `${message.content.slice(0, maxLength)}...`
        : message.content
  }
}

/**
 * Get toast title based on message type
 */
function getToastTitle(message: MessageRecord): string {
  // TODO: Resolve creator_id to display name via user profile lookup
  // For now, use a generic title with creator ID prefix
  const senderPrefix = message.creator_id.slice(0, 8)

  switch (message.type) {
    case 'Media':
      return `New media from ${senderPrefix}...`
    case 'Composition':
      return `New composition from ${senderPrefix}...`
    case 'Message':
    default:
      return `Message from ${senderPrefix}...`
  }
}

/**
 * Hook to display toast notifications for new broadcast messages
 *
 * This hook subscribes to the broadcast store and shows toast notifications
 * when new messages arrive. It supports filtering to exclude certain messages
 * (e.g., messages from the currently visible conversation).
 *
 * @example
 * // Show all message toasts (default behavior)
 * function Dashboard() {
 *   useMessageToast()
 *   return <div>...</div>
 * }
 *
 * @example
 * // Exclude messages from current conversation (chat page)
 * function ChatPage({ conversationId }: { conversationId: string }) {
 *   useMessageToast({
 *     filter: (msg) => msg.conversation_id !== conversationId
 *   })
 *   return <div>...</div>
 * }
 *
 * @example
 * // Temporarily disable toasts (focus mode)
 * function FocusMode() {
 *   const [focusMode, setFocusMode] = useState(false)
 *   useMessageToast({ enabled: !focusMode })
 *   return <div>...</div>
 * }
 */
export function useMessageToast(options?: UseMessageToastOptions): void {
  const { enabled = true, filter, duration = 4000 } = options ?? {}

  // Subscribe to the messages Map from the store
  const messages = useBroadcastStore((state) => state.messages)

  // Track previous message IDs to detect new arrivals
  const prevMessageIdsRef = useRef<Set<string> | null>(null)

  // Track if this is the initial mount (don't toast for existing messages)
  const isInitializedRef = useRef(false)

  useEffect(() => {
    // Skip if disabled
    if (!enabled) {
      return
    }

    // Get current message IDs
    const currentIds = new Set(messages.keys())

    // On first run, just capture the initial state (no toasts)
    if (!isInitializedRef.current) {
      prevMessageIdsRef.current = currentIds
      isInitializedRef.current = true
      return
    }

    // Find new message IDs (in current but not in previous)
    const previousIds = prevMessageIdsRef.current ?? new Set<string>()
    const newMessageIds: string[] = []

    currentIds.forEach((id) => {
      if (!previousIds.has(id)) {
        newMessageIds.push(id)
      }
    })

    // Show toast for each new message
    newMessageIds.forEach((id) => {
      const message = messages.get(id)
      if (!message) return

      // Apply filter if provided
      if (filter && !filter(message)) {
        return
      }

      // Skip deleted messages
      if (message.is_deleted) {
        return
      }

      // Show toast notification
      toast(getToastTitle(message), {
        description: formatMessagePreview(message),
        duration,
      })
    })

    // Update ref with current state for next comparison
    prevMessageIdsRef.current = currentIds
  }, [messages, enabled, filter, duration])

  // Reset initialization when enabled changes from false to true
  useEffect(() => {
    if (!enabled) {
      isInitializedRef.current = false
      prevMessageIdsRef.current = null
    }
  }, [enabled])
}
